import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { Menu, MenuListValue } from '../../../models/api-response.model';
import { MenuService, MenuTypeFilter } from '../../../services/menu_service';

type PageToken = number | '...';

@Component({
  selector: 'app-menus',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './menus.component.html',
  styleUrls: ['./menus.component.scss']
})
export class MenusComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly menuService = inject(MenuService);

  readonly menus = signal<Menu[]>([]);
  readonly parentMenus = signal<Menu[]>([]);
  readonly searchTerm = signal('');
  readonly selectedType = signal<MenuTypeFilter>('ALL');
  readonly currentPage = signal(1);
  readonly pageSize = signal(10);
  readonly totalItems = signal(0);
  readonly totalPages = signal(1);
  readonly isFormOpen = signal(false);
  readonly editingMenuId = signal<number | null>(null);
  readonly toast = signal<{ message: string; type: 'success' | 'error' } | null>(null);

  readonly pageSizeOptions = [10, 25, 50];

  readonly pageTokens = computed(() => this.buildPageTokens());
  readonly showingStart = computed(() => (this.totalItems() === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1));
  readonly showingEnd = computed(() => (this.totalItems() === 0 ? 0 : Math.min(this.currentPage() * this.pageSize(), this.totalItems())));

  readonly form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
    css_class: [''],
    route_url: [''],
    order_no: [1, [Validators.required, Validators.min(1)]],
    parent_menu_id: [0]
  });

  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.loadMenus(1);
    this.loadParentMenus();
  }

  openCreateForm(): void {
    this.showToast(null);
    this.editingMenuId.set(null);
    this.form.reset({
      name: '',
      css_class: '',
      route_url: '',
      order_no: 1,
      parent_menu_id: 0
    });
    this.isFormOpen.set(true);
  }

  edit(menu: Menu): void {
    this.showToast(null);
    this.editingMenuId.set(menu.id);
    this.form.reset({
      name: menu.name,
      css_class: menu.css_class || '',
      route_url: menu.route_url || '',
      order_no: menu.order_no,
      parent_menu_id: menu.parent_menu_id || 0
    });
    this.isFormOpen.set(true);
  }

  closeForm(): void {
    this.isFormOpen.set(false);
    this.editingMenuId.set(null);
    this.form.reset({
      name: '',
      css_class: '',
      route_url: '',
      order_no: 1,
      parent_menu_id: 0
    });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const rawValue = this.form.getRawValue();
    const payload = {
      name: rawValue.name,
      css_class: rawValue.css_class || null,
      route_url: rawValue.route_url || null,
      order_no: rawValue.order_no,
      parent_menu_id: rawValue.parent_menu_id || null
    };

    const menuId = this.editingMenuId();
    const request = menuId === null
      ? this.menuService.create(payload)
      : this.menuService.update(menuId, payload);

    request.subscribe({
      next: () => {
        this.showToast(menuId === null ? 'Menu created successfully.' : 'Menu updated successfully.', 'success');
        this.closeForm();
        this.loadMenus(this.currentPage());
        this.loadParentMenus();
      },
      error: (error: HttpErrorResponse) => {
        this.showToast(error.error?.message || 'Unable to save menu.', 'error');
      }
    });
  }

  remove(menu: Menu): void {
    if (!confirm(`Delete menu "${menu.name}"?`)) {
      return;
    }

    this.menuService.delete(menu.id).subscribe({
      next: () => {
        this.showToast('Menu deleted successfully.', 'success');
        this.loadMenus(this.currentPage());
        this.loadParentMenus();
      },
      error: (error: HttpErrorResponse) => {
        this.showToast(error.error?.message || 'Unable to delete menu.', 'error');
      }
    });
  }

  onSearch(value: string): void {
    this.searchTerm.set(value);
    this.loadMenus(1);
  }

  onTypeChange(value: string): void {
    const nextType = this.normalizeMenuType(value);
    this.selectedType.set(nextType);
    this.loadMenus(1);
  }

  onPageSizeChange(value: string): void {
    const nextSize = Number(value) || 10;
    this.pageSize.set(nextSize);
    this.loadMenus(1);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages()) {
      return;
    }
    this.loadMenus(page);
  }

  prevPage(): void {
    this.goToPage(this.currentPage() - 1);
  }

  nextPage(): void {
    this.goToPage(this.currentPage() + 1);
  }

  typeLabel(menu: Menu): string {
    return menu.parent_menu_id ? 'Child' : 'Parent';
  }

  statusLabel(menu: Menu): string {
    return menu.is_active ? 'Active' : 'Inactive';
  }

  parentLabel(menu: Menu): string {
    return menu.parent_menu?.name || '-';
  }

  private loadMenus(page: number): void {
    this.menuService.list({
      search: this.searchTerm().trim() || undefined,
      menuType: this.selectedType(),
      page,
      pageSize: this.pageSize()
    }).subscribe({
      next: (response) => {
        const value = this.extractMenuListValue(response.value);
        this.menus.set(value.items);
        this.currentPage.set(value.pagination.page);
        this.pageSize.set(value.pagination.page_size);
        this.totalItems.set(value.pagination.total_items);
        this.totalPages.set(value.pagination.total_pages);
      },
      error: (error: HttpErrorResponse) => {
        this.showToast(error.error?.message || 'Unable to load menus.', 'error');
      }
    });
  }

  private loadParentMenus(): void {
    this.menuService.list({
      menuType: 'PARENT',
      page: 1,
      pageSize: 500
    }).subscribe({
      next: (response) => {
        const value = this.extractMenuListValue(response.value);
        this.parentMenus.set(value.items.filter((menu) => menu.is_active));
      },
      error: () => {
        this.parentMenus.set([]);
      }
    });
  }

  private buildPageTokens(): PageToken[] {
    const totalPages = this.totalPages();
    const currentPage = this.currentPage();

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const tokens: PageToken[] = [1];
    const startPage = currentPage <= 4 ? 2 : currentPage - 1;
    const endPage = currentPage >= totalPages - 3 ? totalPages - 1 : currentPage + 1;

    if (startPage > 2) {
      tokens.push('...');
    }

    for (let page = startPage; page <= endPage; page += 1) {
      tokens.push(page);
    }

    if (endPage < totalPages - 1) {
      tokens.push('...');
    }

    tokens.push(totalPages);
    return tokens;
  }

  private normalizeMenuType(value: string): MenuTypeFilter {
    switch (value) {
      case 'PARENT':
      case 'CHILD':
        return value;
      default:
        return 'ALL';
    }
  }

  private extractMenuListValue(value: MenuListValue | ''): MenuListValue {
    if (value === '') {
      return {
        items: [],
        pagination: {
          page: 1,
          page_size: this.pageSize(),
          total_items: 0,
          total_pages: 1
        }
      };
    }

    return value;
  }

  private showToast(message: string | null, type: 'success' | 'error' = 'success'): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    if (!message) {
      this.toast.set(null);
      return;
    }

    this.toast.set({ message, type });
    this.toastTimer = setTimeout(() => this.toast.set(null), 3000);
  }
}
